import React, { useEffect, useMemo, useRef, useState } from "react"

const LOCAL_STORAGE_TTL_MS = 60 * 60 * 1000

function tokenFromQuery() {
  const params = new URLSearchParams(window.location.search)
  return params.get("token") || ""
}

function gateIdFromPathname(pathname) {
  const parts = pathname.split("/").filter(Boolean)
  if (parts.length >= 2 && parts[0] === "help" && /^[0-9a-fA-F-]{36}$/.test(parts[1])) return parts[1]
  return ""
}

function storageKey(token, gateId) {
  if (token) return `helpChatSessionId:${token}`
  if (gateId) return `helpChatSessionId:gate:${gateId}`
  return "helpChatSessionId:anon"
}

function identityStorageKey(gateId) {
  return gateId ? `helpIdentity:gate:${gateId}` : "helpIdentity:anon"
}

function isExpired(savedAt) {
  if (!savedAt) return true
  return Number(savedAt) + LOCAL_STORAGE_TTL_MS < Date.now()
}

function readIdentityFromStorage(key) {
  const raw = window.localStorage.getItem(key)
  if (!raw) return { rego: "", email: "", submitted: false }

  try {
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== "object" || isExpired(parsed.saved_at)) {
      window.localStorage.removeItem(key)
      return { rego: "", email: "", submitted: false }
    }

    const rego = (parsed.rego || "").toUpperCase()
    const email = (parsed.email || "").toLowerCase()
    return { rego, email, submitted: Boolean(rego && email) }
  } catch {
    window.localStorage.removeItem(key)
    return { rego: "", email: "", submitted: false }
  }
}

function readChatSessionIdFromStorage(key) {
  const raw = window.localStorage.getItem(key)
  if (!raw) return ""

  try {
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== "object" || isExpired(parsed.saved_at)) {
      window.localStorage.removeItem(key)
      return ""
    }
    return parsed.value || ""
  } catch {
    window.localStorage.removeItem(key)
    return ""
  }
}

async function postChat(payload, gateId) {
  if (!gateId) {
    throw new Error("Invalid gate link. Please scan the gate QR code again.")
  }

  const endpoint = `/help/${gateId}/chat.json`
  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json"
    },
    body: JSON.stringify(payload)
  })

  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new Error(data.error || "Unable to send message")
  }

  return data
}

function renderMessageWithLinks(content) {
  const text = content || ""
  const parts = text.split(/(https?:\/\/[^\s]+)/g)
  return parts.map((part, index) => {
    if (part.match(/^https?:\/\//)) {
      const cleanHref = part.replace(/[.,!?;:]+$/, "")
      const trailing = part.slice(cleanHref.length)
      const label = /\/parking\/\d+/i.test(cleanHref) ? "Click Here" : cleanHref
      return (
        <React.Fragment key={`link-${index}`}>
          <a href={cleanHref} target="_blank" rel="noopener noreferrer">
            {label}
          </a>
          {trailing}
        </React.Fragment>
      )
    }
    return <React.Fragment key={`text-${index}`}>{part}</React.Fragment>
  })
}

export default function HelpApp() {
  const token = useMemo(() => tokenFromQuery(), [])
  const gateId = useMemo(() => gateIdFromPathname(window.location.pathname), [])
  const identityKey = useMemo(() => identityStorageKey(gateId), [gateId])
  const initialIdentity = useMemo(() => readIdentityFromStorage(identityKey), [identityKey])
  const [rego, setRego] = useState(initialIdentity.rego)
  const [email, setEmail] = useState(initialIdentity.email)
  const [identitySubmitted, setIdentitySubmitted] = useState(initialIdentity.submitted)
  const sessionKey = useMemo(() => {
    const cleanedRego = rego.replace(/\s+/g, "").toUpperCase()
    const cleanedEmail = email.trim().toLowerCase()
    const keySeed = cleanedRego && cleanedEmail ? `${cleanedRego}:${cleanedEmail}` : null
    return storageKey(token, gateId + (keySeed ? `:${keySeed}` : ""))
  }, [email, gateId, rego, token])
  const [chatSessionId, setChatSessionId] = useState(() => readChatSessionIdFromStorage(sessionKey))
  const [messages, setMessages] = useState([])
  const [message, setMessage] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const listRef = useRef(null)

  useEffect(() => {
    if (!chatSessionId) {
      window.localStorage.removeItem(sessionKey)
      return
    }
    window.localStorage.setItem(sessionKey, JSON.stringify({
      value: chatSessionId,
      saved_at: Date.now()
    }))
  }, [chatSessionId, sessionKey])

  useEffect(() => {
    if (!identitySubmitted) return

    window.localStorage.setItem(identityKey, JSON.stringify({
      rego: rego.replace(/\s+/g, "").toUpperCase(),
      email: email.trim().toLowerCase(),
      saved_at: Date.now()
    }))
  }, [email, identityKey, identitySubmitted, rego])

  useEffect(() => {
    const identity = readIdentityFromStorage(identityKey)
    setRego(identity.rego)
    setEmail(identity.email)
    setIdentitySubmitted(identity.submitted)

    const cleanedRego = identity.rego.replace(/\s+/g, "").toUpperCase()
    const cleanedEmail = identity.email.trim().toLowerCase()
    const keySeed = cleanedRego && cleanedEmail ? `${cleanedRego}:${cleanedEmail}` : null
    const restoredSessionKey = storageKey(token, gateId + (keySeed ? `:${keySeed}` : ""))
    setChatSessionId(readChatSessionIdFromStorage(restoredSessionKey))
    setMessages([])
  }, [gateId, identityKey, token])

  useEffect(() => {
    if (!listRef.current) return
    listRef.current.scrollTop = listRef.current.scrollHeight
  }, [messages, loading])

  async function handleSend(event) {
    event.preventDefault()
    const text = message.trim()
    if (!text || loading) return
    if (!identitySubmitted) {
      setError("Enter rego and email to start chat")
      return
    }

    const optimisticMessage = {
      id: `tmp-${Date.now()}`,
      role: "user",
      content: text
    }

    setMessages((previous) => [...previous, optimisticMessage])
    setMessage("")
    setError("")
    setLoading(true)

    try {
      const data = await postChat({
        token: token || null,
        gate_id: gateId || null,
        plate_number: rego,
        email,
        chat_session_id: chatSessionId || null,
        message: text
      }, gateId)

      setChatSessionId(data.chat_session_id || "")
      setMessages(Array.isArray(data.messages) ? data.messages : [])
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setLoading(false)
    }
  }

  function handleStartNewSession() {
    window.localStorage.removeItem(identityKey)
    window.localStorage.removeItem(sessionKey)
    setRego("")
    setEmail("")
    setIdentitySubmitted(false)
    setChatSessionId("")
    setMessages([])
    setMessage("")
    setError("")
    setLoading(false)
  }

  function handleIdentitySubmit(event) {
    event.preventDefault()
    const cleanedRego = rego.replace(/\s+/g, "").toUpperCase()
    const cleanedEmail = email.trim().toLowerCase()
    if (!cleanedRego) {
      setError("Enter your rego")
      return
    }
    if (!cleanedEmail) {
      setError("Enter your email address")
      return
    }
    if (!/^[^\s@]+@[^\s@]+$/.test(cleanedEmail)) {
      setError("Enter a valid email address")
      return
    }
    setRego(cleanedRego)
    setEmail(cleanedEmail)
    setError("")
    setIdentitySubmitted(true)
  }

  return (
    <div className="help-shell">
      <header className="help-header">
        <div className="help-header-top">
          <h1>AI Carpark Attendant</h1>
          {identitySubmitted ? (
            <button type="button" className="help-reset-button" onClick={handleStartNewSession}>
              Start New Session
            </button>
          ) : null}
        </div>
        <p>Ask for help with payment, session status, or opening the gate.</p>
      </header>

      <div className={`help-main ${identitySubmitted ? "help-main-chat-only" : ""}`}>
        {!identitySubmitted ? (
          <form className="help-identity-form" onSubmit={handleIdentitySubmit}>
            <p className="help-identity-title">Before we begin</p>
            <p className="help-identity-copy">Enter your rego and email so we can identify your session.</p>
            <div className="help-identity-grid">
              <input
                type="text"
                value={rego}
                onChange={(e) => setRego(e.target.value.replace(/\s+/g, "").toUpperCase())}
                placeholder="Rego"
                autoComplete="off"
              />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                autoComplete="email"
              />
            </div>
            <button type="submit" className="help-identity-button">Start Chat</button>
          </form>
        ) : null}

        <section className="help-chat" ref={listRef}>
          {!identitySubmitted ? (
            <p className="help-empty">Start chat by entering your rego and email.</p>
          ) : messages.length === 0 ? (
            <p className="help-empty">Start by describing the issue you are seeing at the exit gate.</p>
          ) : (
          messages.map((entry) => (
            <article key={entry.id} className={`help-message help-message-${entry.role}`}>
              <p>{renderMessageWithLinks(entry.content)}</p>
            </article>
          ))
        )}

          {loading ? <p className="help-loading">Thinking...</p> : null}
        </section>
      </div>

      <form className="help-form" onSubmit={handleSend}>
        {error ? <p className="help-error">{error}</p> : null}
        <div className="help-input-row">
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Type your message"
            autoComplete="off"
            disabled={!identitySubmitted}
          />
          <button type="submit" disabled={!identitySubmitted || loading || !message.trim()}>
            Send
          </button>
        </div>
      </form>
    </div>
  )
}
